import { Component, EventEmitter, HostBinding, Input, OnInit, Output } from '@angular/core';
import { LocalizationService } from '../../services/localization.service';
import { AuthAvailability, AuthService, AuthUser } from '../../services/auth.service';
import { EnvironmentService } from '../../services/environment.service';
import { DataService, UserProfileData } from '../../services/data.service';

@Component({
    selector: 'app-pg-customer-care-settings',
    templateUrl: './pg-customer-care-settings.component.html',
    styleUrls: ['./pg-customer-care-settings.component.scss'],
    standalone: false
})
export class PgCustomerCareSettingsComponent implements OnInit {

    constructor(private environmentService:EnvironmentService, private localizationService:LocalizationService, private dataService:DataService, private authService:AuthService) { }

    @Input() userId:string;
    @Input() userProfile:UserProfileData;
    
    @Input() keepOpen:boolean;

    @Output() editOpenChange = new EventEmitter<boolean>();

    @HostBinding('class.PGCustomerCareSettingsHost--EditOpen')
    get hostEditOpen() { return this.isEditOpen; }

    hasCustomerCare = true;
    isEditOpen = false;

    customerCareLanguageOptions:Array<{ value: string, text: string }> = null;
    customerCareChannelOptions:Array<{ value: string, text: string }> = null;

    isLoadingProfile = false;
    isLoadingAvailability = false;

    user:AuthUser = null;
    profile:UserProfileData = null;
    availability:AuthAvailability = null;
    totemData:Array<any> = null;

    ngOnInit(): void {
        this.isLoadingProfile = true;

        this.loadData().then(() => {
            this.isLoadingProfile = false;

            this.hasCustomerCare = (this.environmentService.isWebsocketEnabled() && this.profile != null && typeof this.profile.customer_care != 'undefined')
        
            this.customerCareLanguageOptions = [];
                
            for(let _language of this.localizationService.availableApplicationLanguages) {
                this.customerCareLanguageOptions.push({ value: _language, text: this.localizationService.languageLabels[_language] })
            }

            this.customerCareChannelOptions = []; 
                
            let _realmIn = [];

            for(let _totem of this.totemData) {
                let _value = '';
                let _text = ''

                if(_totem.realm_id != null) {
                    _value = _totem.realm_id;
                    _text = '#' + this.environmentService.getRealmName(_totem.realm_id)
                }
                else {
                    _value = 'global';
                    _text =  '#Global';
                }

                if(_totem.channel != null) {
                    _value += '.' + _totem.channel
                    _text += ' - ' + _totem.channel;
                }

                if(_realmIn.indexOf(_value) == -1) {
                    _realmIn.push(_value)
                    this.customerCareChannelOptions.push({ value: _value, text: _text })
                }
            }

            this.customerCareChannelOptions.sort((a, b) => {
                if(a.value < b.value) return -1;
                else if(a.value > b.value) return 1;
                else return 0;
            })

            this.checkLoadAvailability(true);
            if(this.keepOpen) {
                this.isEditOpen = false;
            }
        })
    }

    toggleEditPanel() {
        if(!this.getUserCustomerCare()) return;
        if(this.isEditOpen) {
            this.closeEditPanel();
        }
        else {
            this.openEditPanel();
        }
    }

    private openEditPanel() {
        this.isEditOpen = true;
        this.editOpenChange.emit(true);
        this.isLoadingAvailability = true;
        this.loadAvailability().then(() => {
            this.isLoadingAvailability = false;
            this._mustLoadAvailability = false;
            this.beginEditAvailability();
        }, () => {
            this.isLoadingAvailability = false;
            this.beginEditAvailability();
        })
    }

    private closeEditPanel() {
        this.isEditOpen = false;
        this.editAvailability = null;
        this.editOpenChange.emit(false);
    }

    getLanguagesPreview() {
        try {
            let _languages:Array<string> = JSON.parse(this.getUserCustomerCareLanguages() || '[]');
            if(_languages == null || _languages.length == 0) {
                return this.localizationService.translate('pg-user-menu.customer-care-languages-none');
            }
            return _languages.map((language) => this.localizationService.languageLabels[language] || language).join(', ');
        }
        catch(ex) {
            return this.localizationService.translate('pg-user-menu.customer-care-languages-none');
        }
    }

    getChannelsPreview() {
        try {
            let _channels:Array<string> = JSON.parse(this.getUserCustomerCareChannels() || '[]');
            if(_channels == null || _channels.length == 0) {
                return this.localizationService.translate('pg-user-menu.customer-care-channels-none');
            }
            return _channels.map((channel) => {
                let _option = this.customerCareChannelOptions?.find((item) => item.value == channel);
                return _option != null ? _option.text : channel;
            }).join(', ');
        }
        catch(ex) {
            return this.localizationService.translate('pg-user-menu.customer-care-channels-none');
        }
    }

    getAvailabilityPreview() {
        // Riusa la formattazione compatta già usata da format.timetable (PGTimetableFormat-Day + badge orari)
        return this.getUserAvailability()
            || ('<span class="text-muted">' + this.localizationService.translate('pg-user-menu.availability-none') + '</span>');
    }

    private _mustLoadAvailability = true;

    checkLoadAvailability(force?:boolean) {
        if(force || this._mustLoadAvailability) {
            this._mustLoadAvailability = false;
            this.isLoadingAvailability = true;
            
            this.loadAvailability().then(() => {
                this.isLoadingAvailability = false;
            }, () => {
                this.isLoadingAvailability = false;
            })
        }
    }

    loadAvailability() {
        return new Promise<void>((resolve, reject) => {
            if(this.userId == null) {
                if(this.authService.isLoggedIn()) {
                    this.availability = this.authService.availability;
                }
                resolve()
            }
            else {
                this.dataService.getUserAvailability(this.userId).subscribe((data) => {
                    this.availability = new AuthAvailability(data);
                    resolve();
                })
            }
        })
    }

    loadData() {
        return new Promise<void>((resolve, reject) => {
            this.dataService.getResourceData('TotemEnvironment', { limit: 5000 }, null, null, 'public').subscribe((data) => {
                this.totemData = data;

                if(this.userId == null) {
                    if(this.authService.isLoggedIn()) {
                        this.user = this.authService.user;
                        this.profile = this.authService.profile;
                        this.availability = this.authService.availability;
                    }
                    resolve()
                }
                else {
                    if(this.userProfile != null) {
                        this.profile = this.userProfile;
                        resolve();
                    }
                    else {
                        this.dataService.getUserData(this.userId).subscribe((data) => {
                            this.user = new AuthUser(this.environmentService.nullRealm, data);
    
                            this.dataService.getUserProfile(this.userId).subscribe((data) => {
                                this.profile = data;
                                
                                resolve();
                            })  
                        })
                    }
                }
            })
        })
    }

    private _getMockAvailabilityHtml() {
        let _days = this.localizationService.data.dayNames;
        let _weekStart = this.localizationService.data.weekStart || 0;
        let _dayRow = '';
        for(let i = 0; i < 7; i++) {
            let _index = (_weekStart + i) % 7;
            let _active = _index >= 1 && _index <= 5; // Lun-Ven
            _dayRow += '<div class="PGTimetableFormat-Day ' + (_active ? 'active' : '') + '">' + _days[_index].charAt(0) + '</div>';
        }

        return ''
            + '<div class="d-flex my-1">' + _dayRow + '</div>'
            + '<div class="PGTimetableFormat-Hours">'
            + '<span class="badge bg-primary me-2">09:00 - 13:00</span>'
            + '<span class="badge bg-primary me-2">14:00 - 18:00</span>'
            + '</div>';
    }

    getUserAvailability() {
        if(this.availability != null && !this.isLoadingAvailability) { 
            let _retVal = this.localizationService.format.timetable('slots', this.availability.slots);

            if(_retVal == '') _retVal = this._getMockAvailabilityHtml();

            return _retVal;
        }

        if(!this.isLoadingAvailability) {
            return this._getMockAvailabilityHtml();
        }
    }

    editAvailability:string = null;

    private beginEditAvailability() {
        if(this.availability != null && this.availability.slots != null) {
            this.editAvailability = JSON.stringify(this.availability.slots.toData());
        }
        else {
            this.editAvailability = '[]';
        }
    }

    setUserAvailabilitySlots(val:string) {
        this.editAvailability = val;
        if(!this.canSetCustomerCare()) return;

        this.isSettingCustomerCare = true;
        let _slots = JSON.parse(val || '[]');

        if(this.userId == null) {
            this.authService.setUserAvailabilitySlots(_slots).then((data) => {
                this.isSettingCustomerCare = false;
                this.availability = new AuthAvailability(data);
            }, () => {
                this.isSettingCustomerCare = false;
            })
        }
        else {
            this.dataService.setUserAvailabilitySlots(_slots, this.userId).subscribe((data) => {
                this.isSettingCustomerCare = false;
                this.availability = new AuthAvailability(data);
            }, () => {
                this.isSettingCustomerCare = false;
            })
        }
    }

    getUserCustomerCare() {
        if(this.profile != null) { 
            return this.profile.customer_care; 
        }
    }

    isSettingCustomerCare = false;

    canSetCustomerCare() {
        return !this.isSettingCustomerCare;
    }

    setUserCustomerCare(val:boolean) {
        if(this.canSetCustomerCare()) {
            this.isSettingCustomerCare = true;

            if(this.userId == null) {
                this.authService.setUserProfile({ customer_care: val }).then((data) => {
                    this.isSettingCustomerCare = false;
                    this.profile = data;
                    if(!val) this.closeEditPanel();
                }, () => {
                    this.isSettingCustomerCare = false;
                })
            }
            else {
                this.dataService.setUserProfile({ customer_care: val }, this.userId).subscribe((data) => {
                    this.isSettingCustomerCare = false;
                    this.profile = data;
                    if(!val) this.closeEditPanel();
                }, () => {
                    this.isSettingCustomerCare = false;
                })
            }
        }
    }

    getUserCustomerCareLanguages() {
        if(this.profile != null) { 
            return this.profile.languages || '[]'; 
        }
        else return '[]'
    }

    setUserCustomerCareLanguages(val:string) {
        if(this.canSetCustomerCare()) {
            this.isSettingCustomerCare = true;
            if(this.userId == null) {
                this.authService.setUserProfile({ languages: val }).then((data) => {
                    this.isSettingCustomerCare = false;
                    this.profile = data;
                }, () => {
                    this.isSettingCustomerCare = false;
                })
            }
            else {
                this.dataService.setUserProfile({ languages: val }, this.userId).subscribe((data) => {
                    this.isSettingCustomerCare = false;
                    this.profile = data;
                }, () => {
                    this.isSettingCustomerCare = false;
                })
            }
        }
    }

    getUserCustomerCareChannels() {
        if(this.availability != null) { 
            return JSON.stringify(this.availability.channels) || '[]'; 
        }
        else return '[]'
    }

    setUserCustomerCareChannels(val:string) {
        if(this.canSetCustomerCare()) {
            this.isSettingCustomerCare = true;
            if(this.userId == null) {
                this.authService.setUserAvailabilityChannels(val).then((data) => {
                    this.isSettingCustomerCare = false;
                    this.availability = new AuthAvailability(data);
                }, () => {
                    this.isSettingCustomerCare = false;
                })
            }
            else {
                this.dataService.setUserAvailabilityChannels(val, this.userId).subscribe((data) => {
                    this.isSettingCustomerCare = false;
                    this.availability = new AuthAvailability(data);
                }, () => {
                    this.isSettingCustomerCare = false;
                })
            }
        }
    }
}
